import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'
import { Fonts, Icon, Container, BtnBack, Button } from 'components/base-ui'
import { isLineVersionHaveBackNavigator } from 'helpers/liff'
import useScript from 'hooks/useScripts'
import { useTranslation } from 'react-i18next'

declare global {
  interface Window {
    Html5Qrcode: any
  }
}

interface ScanQRCodeProps {
  title: string
  show: boolean
  onScanCompleted: (code: string) => void
  onBack: () => void
  onScanFailed?: () => void
}

const ScanQRCode = ({ title, show, onScanCompleted, onBack, onScanFailed }: ScanQRCodeProps) => {
  useScript('/scripts/html5-qrcode.min.js')
  const html5QRCodeRef = useRef<any>(null)
  const html5QRCodeImageRef = useRef<any>(null)

  const _isLineVersionHaveBackNavigator = isLineVersionHaveBackNavigator()
  const { t } = useTranslation()

  useEffect(() => {
    const fileinput = document.getElementById('qr-input-file')

    if (show) {
      setTimeout(() => {
        html5QRCodeRef.current = new window.Html5Qrcode('reader')
        html5QRCodeImageRef.current = new window.Html5Qrcode('reader-image', {
          experimentalFeatures: {
            useBarCodeDetectorIfSupported: true,
          },
        })

        const mediaTrackConstraintsConfig = {
          videoConstraints: {
            facingMode: { exact: 'environment' },
            height: 500,
          },
        }

        html5QRCodeRef.current.start({}, mediaTrackConstraintsConfig, qrCodeSuccessCallback).catch((error) => {
          if (!html5QRCodeRef.current.canvasElement) {
            html5QRCodeRef.current.start({}, mediaTrackConstraintsConfig, qrCodeSuccessCallback)
          }
        })

        fileinput && fileinput.addEventListener('change', scanCodeFromImage)
      }, 300)
    } else {
      if (html5QRCodeRef.current) {
        html5QRCodeRef.current
          .stop()
          .then((ignore) => {
            // QR Code scanning is stopped.
          })
          .catch((err) => {
            // Stop failed, handle it.
          })
      }

      fileinput && fileinput.removeEventListener('change', scanCodeFromImage)
    }

    return () => {
      if (html5QRCodeRef.current) {
        html5QRCodeRef.current
          .stop()
          .then((ignore) => {
            // QR Code scanning is stopped.
          })
          .catch((err) => {
            // Stop failed, handle it.
          })
      }

      if (html5QRCodeImageRef.current) {
        html5QRCodeImageRef.current.clear()
      }

      fileinput && fileinput.removeEventListener('change', scanCodeFromImage)
    }
  }, [show])

  return (
    <ScanQRCodeContainer>
      <HeaderNav>
        {!_isLineVersionHaveBackNavigator && (
          <Container>
            <BtnBack onClick={onBack} />
          </Container>
        )}
      </HeaderNav>

      <CameraPreview id="reader-image"></CameraPreview>
      <CameraPreview id="reader"></CameraPreview>

      <FilmContainer>
        <FilmContainerWhite />
      </FilmContainer>

      <FilmContainerWhiteGhost>
        <LabelTextScan>
          <Icon name="scan-barcode" />
          <Fonts.Medium>{title}</Fonts.Medium>
        </LabelTextScan>
      </FilmContainerWhiteGhost>

      <ButtonConfirmCouponWrapper>
        <Button
          minWidth="210px"
          md2
          onClick={() => {
            document.getElementById('qr-input-file')?.click()
          }}
        >
          <Icon name="upload" />
          {t('buttonUploadImage')}
        </Button>
        <input type="file" id="qr-input-file" accept="image/*" />
      </ButtonConfirmCouponWrapper>
    </ScanQRCodeContainer>
  )

  function handleOnCompletedScanQRCode(decodedText: string) {
    onScanCompleted(decodedText)
  }

  function qrCodeSuccessCallback(decodedText, decodedResult) {
    handleOnCompletedScanQRCode(decodedText)
  }

  function scanCodeFromImage(e) {
    if (e.target.files.length === 0) {
      // No file selected, ignore
      return
    }

    const imageFile = e.target.files[0]
    // Scan QR Code
    html5QRCodeImageRef.current
      .scanFile(imageFile, true)
      .then((decodedText) => {
        // success, use decodedText
        handleOnCompletedScanQRCode(decodedText)
      })
      .catch((err) => {
        // failure, handle it.
        if (onScanFailed) {
          onScanFailed()
        } else {
          alert('Scan Image Error')
          console.log(`Error scanning file. Reason: ${err}`)
        }
      })
  }
}

export default ScanQRCode

const HeaderNav = styled.div`
  position: absolute;
  padding-top: 17px;
  z-index: 10;
  margin-bottom: 11px;

  ${Container} {
    display: flex;
    justify-content: space-between;
  }
`

const ScanQRCodeContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  background-color: black;

  #qr-shaded-region {
    opacity: 0;
  }

  #qr-input-file {
    display: none;
  }
`

const CameraPreview = styled.div`
  position: absolute !important;
  width: 100vw;
  height: 100dvh;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`

const FilmContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  background-color: black;
  mix-blend-mode: multiply;
  opacity: 0.5;
`

const FilmContainerWhite = styled.div`
  position: absolute;
  top: 50%;
  left: 40px;
  right: 40px;
  border-radius: 10px;
  background-color: white;
  transform: translatey(-50%);
  margin-top: -30px;

  &::before {
    content: '';
    display: block;
    padding-top: 70%;
  }
`

const FilmContainerWhiteGhost = styled.div`
  position: absolute;
  top: 50%;
  left: 40px;
  right: 40px;
  border-radius: 10px;
  background-color: transparent;
  transform: translatey(-50%);
  margin-top: -30px;

  &::before {
    content: '';
    display: block;
    padding-top: 70%;
  }
`

const LabelTextScan = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  text-align: center;
  margin-top: 15px;
  color: white;
  z-index: 2;

  i {
    margin-right: 10px;
  }
`

const ButtonConfirmCouponWrapper = styled.div`
  display: flex;
  justify-content: center;
  position: fixed;
  bottom: 30px;
  left: 0;
  right: 0;

  .iphoneX & {
    padding-bottom: 50px;
  }

  i {
    margin-right: 8px;
  }
`
